/**
 * NotesTimeline — compact horizontal strip that summarises the notes
 * activity on a lead ("Atendimento nota IA há 3h · Consultor aplicou
 * proposta há 45m · Consultor Zoho há 5m"). Visible to all roles so
 * the Gestor can see the full workflow at a glance without opening
 * the Zoho or scrolling through the note list.
 *
 * Rules:
 *  - Reads ``lead.notes`` (already loaded on the page — no extra API).
 *  - Groups by (author_role, kind) to keep the strip short; keeps the
 *    LATEST timestamp for each group.
 *  - Legacy notes without ``author_role`` are ignored (matches the
 *    display rule elsewhere in the app).
 *  - Renders a horizontal pill row that wraps on small screens.
 */
import { useMemo } from "react";
import {
  Headphones, Stethoscope, Sparkles, FileText, CheckCircle2, Clock,
} from "lucide-react";

function relTime(iso) {
  if (!iso) return "";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.round(diff / 60000);
    if (mins < 1) return "agora";
    if (mins < 60) return `há ${mins}m`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `há ${hrs}h`;
    const days = Math.round(hrs / 24);
    if (days < 30) return `há ${days}d`;
    return new Date(iso).toLocaleDateString("pt-PT");
  } catch { return ""; }
}

// Label + icon + colour for each (role, kind) combination we care
// about. Anything else is bucketed as "outra" and skipped.
const BUCKETS = [
  {
    key: "aten_manual",
    match: (n) => n.author_role === "atendimento" && n.kind === "manual",
    label: "Atendimento · nota",
    Icon: Headphones,
    tone: "blue",
  },
  {
    key: "aten_ai",
    match: (n) => n.author_role === "atendimento" && n.kind === "ai_estimate",
    label: "Atendimento · nota IA → Estimativa",
    Icon: Sparkles,
    tone: "blue",
  },
  {
    key: "cons_manual",
    match: (n) => (n.author_role === "consultor" || n.author_role === "gestor") && n.kind === "manual",
    label: "Consultor · nota Zoho",
    Icon: Stethoscope,
    tone: "green",
  },
  {
    key: "cons_ai",
    match: (n) => (n.author_role === "consultor" || n.author_role === "gestor") && n.kind === "ai_proposal",
    label: "Consultor · aplicou proposta",
    Icon: CheckCircle2,
    tone: "green",
  },
  {
    key: "cons_summary",
    match: (n) => (n.author_role === "consultor" || n.author_role === "gestor") && n.kind === "ai_summary",
    label: "Consultor · resumo IA",
    Icon: Sparkles,
    tone: "green",
  },
];

const TONES = {
  blue: "bg-[hsl(var(--info-bg))] border-[#BFDBFE] text-primary",
  green: "bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/25 text-[hsl(var(--success))]",
  slate: "bg-muted/40 border-border text-muted-foreground",
};

export default function NotesTimeline({ lead }) {
  const items = useMemo(() => {
    const notes = Array.isArray(lead?.notes) ? lead.notes : [];
    const byBucket = new Map();
    for (const n of notes) {
      if (!n.author_role || !n.created_at) continue;
      for (const b of BUCKETS) {
        if (b.match(n)) {
          const cur = byBucket.get(b.key);
          if (!cur || new Date(n.created_at) > new Date(cur.at)) {
            byBucket.set(b.key, {
              bucket: b,
              at: n.created_at,
              author: (n.author_name || "").replace(/ \(via IA\)$/i, ""),
              count: (cur?.count || 0) + 1,
            });
          } else {
            byBucket.set(b.key, { ...cur, count: (cur?.count || 0) + 1 });
          }
          break;
        }
      }
    }
    // Sort chronologically (most recent first)
    return Array.from(byBucket.values()).sort((a, b) =>
      new Date(b.at).getTime() - new Date(a.at).getTime()
    );
  }, [lead?.notes]);

  if (!items.length) return null;

  return (
    <div
      data-testid="notes-timeline"
      className="flex items-center gap-2 flex-wrap bg-card border border-border rounded-2xl p-3 shadow-sm"
    >
      <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground pr-1">
        <Clock className="w-3 h-3" />
        Actividade recente
      </div>
      {items.map((it, idx) => {
        const B = it.bucket;
        const Icon = B.Icon;
        return (
          <div
            key={B.key}
            data-testid={`notes-timeline-item-${B.key}`}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${TONES[B.tone] || TONES.slate}`}
            title={`${B.label} — por ${it.author || "—"} em ${new Date(it.at).toLocaleString("pt-PT")}`}
          >
            <Icon className="w-3 h-3 flex-shrink-0" />
            <span>{B.label}</span>
            {it.count > 1 && (
              <span className="opacity-70">×{it.count}</span>
            )}
            <span className="opacity-70">·</span>
            <span className="opacity-80">{relTime(it.at)}</span>
          </div>
        );
      })}
    </div>
  );
}
